import { Card, CardContent, CardHeader } from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@brent/core-ui/ui'
import { Edit, Trash2, MoreVertical, Music } from 'lucide-react'
import type { Suggestion } from '@/services/api/suggestions'

interface SuggestionCardProps {
  suggestion: Suggestion
  onEdit: (suggestion: Suggestion) => void
  onDelete: (suggestion: Suggestion) => void
}

export const SuggestionCard = ({ suggestion, onEdit, onDelete }: SuggestionCardProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg sm:text-xl font-semibold">{suggestion.title}</h3>
              {suggestion.is_work_freetext && (
                <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                  Custom Entry
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm text-muted-foreground">by {suggestion.composer}</p>
              {suggestion.is_composer_freetext && (
                <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                  Custom Entry
                </span>
              )}
            </div>
          </div>
          {/* Mobile: Dropdown menu */}
          <div className="md:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11">
                  <MoreVertical className="h-5 w-5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(suggestion)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete(suggestion)} className="text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* Desktop: Icon buttons */}
          <div className="hidden md:flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(suggestion)}
              aria-label="Edit suggestion"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(suggestion)}
              aria-label="Delete suggestion"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {suggestion.comment && (
          <p className="text-sm text-muted-foreground mb-2">{suggestion.comment}</p>
        )}

        {/* Performance History */}
        {suggestion.performance_history && suggestion.performance_history.performance_count > 0 && (
          <div className="mb-2 p-3 bg-muted/50 rounded-lg">
            {suggestion.performance_history.performances.length > 0 ? (
              <details className="text-sm">
                <summary className="cursor-pointer flex items-center gap-2 hover:text-primary">
                  <Music className="h-4 w-4 text-primary" />
                  <span className="font-medium">
                    Performed {suggestion.performance_history.performance_count} time
                    {suggestion.performance_history.performance_count !== 1 && 's'}
                  </span>
                </summary>
                <ul className="mt-2 space-y-1 pl-6 text-xs text-muted-foreground">
                  {suggestion.performance_history.performances.slice(0, 5).map((perf, idx) => (
                    <li key={idx}>
                      {perf.concert_date && formatDate(perf.concert_date)}
                      {perf.venue && ` at ${perf.venue}`}
                      {perf.concert_title && ` - ${perf.concert_title}`}
                    </li>
                  ))}
                </ul>
              </details>
            ) : (
              <div className="flex items-center gap-2 text-sm">
                <Music className="h-4 w-4 text-primary" />
                <span className="font-medium">
                  Performed {suggestion.performance_history.performance_count} time
                  {suggestion.performance_history.performance_count !== 1 && 's'}
                </span>
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Suggested on {formatDate(suggestion.created_at)}
        </p>
      </CardContent>
    </Card>
  )
}
